import { useEffect, useState } from 'react';

/**
 * Viewport tier used by the responsive Phase 1 work.
 *
 *   - 'mobile'  : `< 640px`
 *   - 'tablet'  : `640px – 1023px`
 *   - 'desktop' : `>= 1024px`
 *
 * The breakpoints intentionally line up with Tailwind's `sm` and `lg`
 * defaults so any responsive class on a child element (`hidden sm:flex`,
 * `hidden lg:block`, …) reads the same boundary as this hook.
 */
export type ViewportTier = 'mobile' | 'tablet' | 'desktop';

/** Tailwind's default `sm` breakpoint. */
export const TABLET_MIN_PX = 640;
/** Tailwind's default `lg` breakpoint. */
export const DESKTOP_MIN_PX = 1024;

function readTier(): ViewportTier {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    // SSR / jsdom-without-matchMedia fall back to desktop so the WBS
    // grid layout matches its existing behaviour.
    return 'desktop';
  }
  if (window.matchMedia(`(min-width: ${DESKTOP_MIN_PX}px)`).matches) {
    return 'desktop';
  }
  if (window.matchMedia(`(min-width: ${TABLET_MIN_PX}px)`).matches) {
    return 'tablet';
  }
  return 'mobile';
}

/**
 * Subscribe to the viewport-tier breakpoint. Re-renders only when the
 * tier flips — within-tier resizes do not trigger a state update.
 */
export function useViewportTier(): ViewportTier {
  const [tier, setTier] = useState<ViewportTier>(readTier);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const desktop = window.matchMedia(`(min-width: ${DESKTOP_MIN_PX}px)`);
    const tablet = window.matchMedia(`(min-width: ${TABLET_MIN_PX}px)`);
    const update = () => {
      if (desktop.matches) setTier('desktop');
      else if (tablet.matches) setTier('tablet');
      else setTier('mobile');
    };
    desktop.addEventListener('change', update);
    tablet.addEventListener('change', update);
    update();
    return () => {
      desktop.removeEventListener('change', update);
      tablet.removeEventListener('change', update);
    };
  }, []);

  return tier;
}
